import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileUploadInput } from "@/components/ui/file-upload-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  DOCUMENT_TYPES,
  useFileUploader,
  DocumentsResponse,
  Document
} from "@/components/features/FileUploader";

interface VisaDocumentFieldProps {
  profile: any;
  setProfile: (profile: any) => void;
  readOnly: boolean;
  loading: boolean;
}

export function VisaDocumentField({
  profile,
  setProfile,
  readOnly,
  loading
}: VisaDocumentFieldProps) {
  const {
    uploadTalentProfileDocument,
    getTalentDocument,
    deleteFile,
    handleViewFile
  } = useFileUploader();
  const [visaDocument, setVisaDocument] = useState<Document | undefined>(
    undefined
  );
  const [uploading, setUploading] = useState(false);
  const [loadingDocumentRead, setLoadingDocumentRead] = useState(false);

  const fetchVisaDocument = useCallback(() => {
    setLoadingDocumentRead(true);
    getTalentDocument(profile.id, DOCUMENT_TYPES.VISA_DOCUMENT).then(
      (res: DocumentsResponse) => {
        if (res.success) {
          setVisaDocument(res.data[0]);
        }
        setLoadingDocumentRead(false);
      }
    );
  }, [profile.id, getTalentDocument]);

  const handleFileUpload = async (file: File) => {
    if (!profile) return;
    setUploading(true);
    const result = await uploadTalentProfileDocument(
      file,
      DOCUMENT_TYPES.VISA_DOCUMENT,
      profile.id,
      {
        values: {
          talent_id: profile.id
        }
      }
    );
    if (result.success) {
      await fetchVisaDocument();
    }
    setUploading(false);
  };

  useEffect(() => {
    if (profile?.id) {
      fetchVisaDocument();
    }
  }, [profile?.id, fetchVisaDocument]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Visum hochladen</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Bitte lade eine Kopie deines Visums / deiner
                  Aufenthaltserlaubnis hoch
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          <FileUploadInput
            currentFileName={visaDocument?.file_name}
            accept=".pdf,.doc,.docx"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
            disabled={readOnly}
            loading={loading || loadingDocumentRead || uploading}
            onClearFile={async () => {
              if (visaDocument) {
                const success = await deleteFile(
                  "profiles",
                  visaDocument.id,
                  visaDocument.file_path
                );
                if (success) {
                  fetchVisaDocument();
                }
              }
            }}
          />
          {visaDocument && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewFile("profiles", visaDocument.file_path)}
            >
              Anzeigen
            </Button>
          )}
        </div>
      </div>
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">Visum gültig bis</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Bis wann ist das Visum gültig?</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <Input
          disabled={readOnly}
          type="date"
          value={
            profile?.visa_validity
              ? new Date(profile.visa_validity).toISOString().split("T")[0]
              : ""
          }
          onChange={(e) =>
            setProfile({
              ...profile,
              visa_validity: e.target.value
                ? new Date(e.target.value).toISOString()
                : ""
            })
          }
          className="w-full"
        />
      </div>
    </div>
  );
}
